import React from 'react';
import { VaultKeTheme } from '../../utils/theme';

const TermsOfService: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
      padding: VaultKeTheme.spacing.xl,
    },
    container: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    header: {
      backgroundColor: VaultKeTheme.colors.surface,
      padding: VaultKeTheme.spacing.lg,
      borderRadius: VaultKeTheme.borderRadius.md,
      marginBottom: VaultKeTheme.spacing.xl,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: VaultKeTheme.shadows.sm,
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: VaultKeTheme.colors.text,
      fontSize: '24px',
      cursor: 'pointer',
      padding: VaultKeTheme.spacing.sm,
      borderRadius: VaultKeTheme.borderRadius.sm,
      transition: 'background-color 0.2s ease',
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      margin: 0,
    },
    content: {
      backgroundColor: VaultKeTheme.colors.surface,
      borderRadius: VaultKeTheme.borderRadius.md,
      padding: VaultKeTheme.spacing.xl,
      boxShadow: VaultKeTheme.shadows.sm,
      maxHeight: '70vh',
      overflowY: 'auto',
    },
    introSection: {
      paddingBottom: VaultKeTheme.spacing.xl,
      borderBottom: `1px solid ${VaultKeTheme.colors.border}`,
      marginBottom: VaultKeTheme.spacing.xl,
    },
    lastUpdated: {
      ...VaultKeTheme.typography.caption,
      color: VaultKeTheme.colors.textSecondary,
      fontStyle: 'italic',
      marginBottom: VaultKeTheme.spacing.md,
    },
    introText: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.text,
      lineHeight: '1.6',
    },
    section: {
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    sectionTitle: {
      ...VaultKeTheme.typography.h3,
      color: VaultKeTheme.colors.primary,
      marginBottom: VaultKeTheme.spacing.lg,
      fontWeight: 'bold',
    },
    sectionContent: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.text,
      lineHeight: '1.7',
      textAlign: 'justify',
    },
    bulletList: {
      marginLeft: VaultKeTheme.spacing.xl,
      marginTop: VaultKeTheme.spacing.sm,
    },
    bulletItem: {
      marginBottom: VaultKeTheme.spacing.sm,
      display: 'flex',
      alignItems: 'flex-start',
      gap: VaultKeTheme.spacing.sm,
    },
    bullet: {
      color: VaultKeTheme.colors.primary,
      fontWeight: 'bold',
      flexShrink: 0,
    },
    footer: {
      borderTop: `1px solid ${VaultKeTheme.colors.border}`,
      paddingTop: VaultKeTheme.spacing.xl,
      marginTop: VaultKeTheme.spacing.xxl,
    },
    footerText: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      textAlign: 'center',
      fontStyle: 'italic',
    },
  };

  const sections = [
    {
      title: "1. ABOUT VAULTKE",
      content: "VaultKe is a digital financial platform licensed to operate in Kenya, providing:",
      bullets: [
        "Chama (investment group) management services",
        "Digital savings and investment solutions",
        "Peer-to-peer lending within groups",
        "Financial literacy and education",
        "Secure transaction processing",
        "Group financial planning tools"
      ],
      additional: "Our mission is to democratize access to financial services and empower Kenyan communities through technology-driven group savings and investment solutions."
    },
    {
      title: "2. ACCEPTANCE OF TERMS",
      content: "By creating an account or using VaultKe services, you agree to:",
      bullets: [
        "These Terms of Service",
        "Our Privacy Policy",
        "All applicable Kenyan laws and regulations",
        "Central Bank of Kenya (CBK) guidelines",
        "Anti-Money Laundering (AML) requirements"
      ],
      additional: "If you do not agree to these terms, you must not use our services. We may update these terms periodically, and continued use constitutes acceptance of changes."
    },
    {
      title: "3. ELIGIBILITY AND REGISTRATION",
      content: "To use VaultKe, you must:",
      bullets: [
        "Be at least 18 years old",
        "Be a Kenyan citizen or legal resident",
        "Provide accurate and complete information",
        "Have a valid Kenyan National ID",
        "Own a registered mobile phone number",
        "Have access to a bank account or mobile money service"
      ],
      additional: "You are responsible for maintaining the confidentiality of your account credentials and all activities under your account."
    },
    {
      title: "4. SERVICES PROVIDED",
      content: "VaultKe facilitates:",
      subsections: [
        {
          title: "Chama Management:",
          bullets: [
            "Group creation and member management",
            "Contribution tracking and reminders",
            "Meeting scheduling and minutes",
            "Financial reporting and transparency"
          ]
        },
        {
          title: "Financial Services:",
          bullets: [
            "Secure money transfers between members",
            "Group savings and investment tracking",
            "Loan applications and approvals within groups",
            "Interest calculations and payment schedules"
          ]
        },
        {
          title: "Additional Features:",
          bullets: [
            "Financial education resources",
            "Communication tools for members",
            "Data analytics and insights"
          ]
        }
      ]
    },
    {
      title: "5. USER RESPONSIBILITIES",
      content: "You agree to:",
      bullets: [
        "Provide accurate and truthful information",
        "Comply with all applicable laws",
        "Use services only for lawful purposes",
        "Respect other users' rights and privacy",
        "Pay all fees and charges promptly",
        "Report suspicious activities immediately",
        "Maintain adequate funds for transactions",
        "Keep your contact information updated"
      ],
      additional: "You must NOT:",
      negativeBullets: [
        "Use the platform for illegal activities",
        "Impersonate others or create fake accounts",
        "Attempt to hack or disrupt our systems",
        "Share your login credentials",
        "Engage in fraudulent transactions"
      ]
    },
    {
      title: "6. FINANCIAL REGULATIONS COMPLIANCE",
      content: "VaultKe operates under Kenyan financial regulations:",
      subsections: [
        {
          title: "Central Bank of Kenya (CBK):",
          bullets: [
            "We comply with payment system regulations",
            "Transaction limits as per CBK guidelines",
            "Regular reporting to regulatory authorities",
            "Anti-money laundering (AML) compliance"
          ]
        },
        {
          title: "Know Your Customer (KYC):",
          bullets: [
            "Identity verification is mandatory",
            "We may request additional documentation",
            "Suspicious activities are reported to FRC",
            "Account monitoring for compliance"
          ]
        },
        {
          title: "Consumer Protection:",
          bullets: [
            "Fair treatment of all users",
            "Transparent fee structures",
            "Complaint resolution mechanisms",
            "Regular audits and assessments"
          ]
        }
      ]
    }
  ];

  const renderSection = (section: any, index: number) => (
    <div key={index} style={styles.section}>
      <h3 style={styles.sectionTitle}>{section.title}</h3>
      <p style={styles.sectionContent}>{section.content}</p>

      {section.bullets && (
        <ul style={styles.bulletList}>
          {section.bullets.map((bullet: string, bulletIndex: number) => (
            <li key={bulletIndex} style={styles.bulletItem}>
              <span style={styles.bullet}>•</span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {section.negativeBullets && (
        <ul style={styles.bulletList}>
          {section.negativeBullets.map((bullet: string, bulletIndex: number) => (
            <li key={bulletIndex} style={styles.bulletItem}>
              <span style={styles.bullet}>•</span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {section.subsections && section.subsections.map((subsection: any, subIndex: number) => (
        <div key={subIndex} style={{ marginTop: VaultKeTheme.spacing.lg }}>
          <h4 style={{ ...VaultKeTheme.typography.h4, color: VaultKeTheme.colors.text, marginBottom: VaultKeTheme.spacing.md }}>
            {subsection.title}
          </h4>
          <ul style={styles.bulletList}>
            {subsection.bullets.map((bullet: string, bulletIndex: number) => (
              <li key={bulletIndex} style={styles.bulletItem}>
                <span style={styles.bullet}>•</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {section.additional && (
        <p style={{ ...styles.sectionContent, marginTop: VaultKeTheme.spacing.lg }}>
          {section.additional}
        </p>
      )}
    </div>
  );

  return (
    <div style={{ ...styles.page, paddingBottom: '80px' }}>
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => window.history.back()}>
            ←
          </button>
          <h1 style={styles.title}>Terms of Service</h1>
          <div style={{ width: '40px' }}></div>
        </div>

        <div style={styles.content}>
          <div style={styles.introSection}>
            <p style={styles.lastUpdated}>Last Updated: January 2025</p>
            <p style={styles.introText}>
              Welcome to VaultKe! These Terms of Service govern your use of our digital financial platform. Please read them carefully.
            </p>
          </div>

          {sections.map(renderSection)}

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>7. FEES AND CHARGES</h3>
            <p style={styles.sectionContent}>
              VaultKe charges fees for certain services:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Transaction fees (as disclosed before each transaction)</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Monthly subscription fees for premium features</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Loan processing fees (percentage of loan amount)</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Late payment penalties as per agreed terms</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Third-party charges (bank fees, mobile money charges)</span></li>
            </ul>
            <p style={{ ...styles.sectionContent, marginTop: VaultKeTheme.spacing.lg }}>
              All fees are clearly disclosed before you incur them. We reserve the right to change fees with 30 days' notice.
            </p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>8. LOANS AND CREDIT</h3>
            <p style={styles.sectionContent}>
              Loan services are subject to:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Group approval and guarantee requirements</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Interest rates as agreed by the chama</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Repayment schedules and penalties</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Credit assessment and approval process</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Reporting to Credit Reference Bureaus (CRBs)</span></li>
            </ul>
            <p style={{ ...styles.sectionContent, marginTop: VaultKeTheme.spacing.lg }}>
              Defaulting on loans may result in:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Account suspension</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Negative credit reporting</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Legal action for recovery</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Exclusion from future credit facilities</span></li>
            </ul>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>9. LIMITATION OF LIABILITY</h3>
            <p style={styles.sectionContent}>
              VaultKe's liability is limited to the extent permitted by Kenyan law:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>We are not liable for losses due to user error</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>System downtime or technical failures</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Third-party service provider issues</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Market fluctuations affecting investments</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Actions of other chama members</span></li>
            </ul>
            <p style={{ ...styles.sectionContent, marginTop: VaultKeTheme.spacing.lg }}>
              Our maximum liability is limited to the fees paid by you in the 12 months preceding the claim.
            </p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>10. DISPUTE RESOLUTION</h3>
            <p style={styles.sectionContent}>
              Disputes will be resolved through:
            </p>
            <ol style={{ ...styles.bulletList, listStyleType: 'decimal', paddingLeft: VaultKeTheme.spacing.xl }}>
              <li style={{ marginBottom: VaultKeTheme.spacing.sm }}>Direct negotiation with our customer service</li>
              <li style={{ marginBottom: VaultKeTheme.spacing.sm }}>Mediation through recognized institutions</li>
              <li style={{ marginBottom: VaultKeTheme.spacing.sm }}>Arbitration under Kenyan Arbitration Act</li>
              <li style={{ marginBottom: VaultKeTheme.spacing.sm }}>Kenyan courts as the final resort</li>
            </ol>
            <p style={{ ...styles.sectionContent, marginTop: VaultKeTheme.spacing.lg }}>
              You may also lodge complaints with:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Central Bank of Kenya (CBK)</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Competition Authority of Kenya</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Consumer Federation of Kenya (COFEK)</span></li>
            </ul>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>11. TERMINATION</h3>
            <p style={styles.sectionContent}>
              Either party may terminate this agreement:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>You may close your account at any time</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>We may suspend/terminate for breach of terms</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Outstanding obligations survive termination</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Data retention as per our Privacy Policy</span></li>
              <li style={styles.bulletItem}><span style={styles.bullet}>•</span><span>Refunds processed as per our refund policy</span></li>
            </ul>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>12. CONTACT INFORMATION</h3>
            <p style={styles.sectionContent}>
              For questions about these terms:
            </p>
            <div style={{ marginLeft: VaultKeTheme.spacing.xl, marginTop: VaultKeTheme.spacing.sm }}>
              <p style={styles.sectionContent}><strong>Email:</strong> legal@vaultke.com</p>
              <p style={styles.sectionContent}><strong>Phone:</strong> +254 700 000 000</p>
              <p style={styles.sectionContent}><strong>Address:</strong> VaultKe Limited, Kisumu, Kenya</p>
              <p style={styles.sectionContent}><strong>Website:</strong> www.vaultke.com</p>
              <p style={styles.sectionContent}><strong>Customer Support:</strong> support@vaultke.com</p>
              <p style={styles.sectionContent}><strong>Business Hours:</strong> Monday - Friday, 8:00 AM - 6:00 PM EAT</p>
            </div>
          </div>

          <div style={styles.footer}>
            <p style={styles.footerText}>
              These Terms of Service are governed by the laws of Kenya. By using VaultKe, you acknowledge that you have read, understood, and agree to be bound by these terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;